import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';

export type DsSectionSurface = 'page' | 'section' | 'inverse' | 'brand';
export type DsSectionContainer = 'narrow' | 'default' | 'wide' | 'full';
export type DsSectionPy = 'sm' | 'md' | 'lg' | 'xl';
export type DsSectionAlign = 'left' | 'center';

@Component({
  selector: 'ds-section',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <section
      [id]="id() ?? null"
      class="ds-section"
      [class]="sectionClasses()"
    >
      <div class="ds-section__container" [class]="containerClass()">
        @if (eyebrow() || title() || lead()) {
          <header
            class="ds-section__header"
            [class]="headerClasses()"
          >
            <div class="ds-section__text" [class]="textClasses()">
              @if (eyebrow()) {
                <div class="ds-section__eyebrow" [class]="'ds-section__eyebrow--' + surface()">
                  {{ eyebrow() }}
                </div>
              }
              @if (title()) {
                <h2 class="ds-section__title">{{ title() }}</h2>
              }
              @if (lead()) {
                <p class="ds-section__lead" [class]="'ds-section__lead--' + surface()">{{ lead() }}</p>
              }
            </div>
          </header>
        }
        <ng-content />
      </div>
    </section>
  `,
  styles: [`
    /* ── Section base ─────────────────────────────────────────── */
    .ds-section {
      font-family: var(--f-body);
    }

    /* ── Surfaces ─────────────────────────────────────────────── */
    .ds-section--page {
      background: var(--surface);
      color: var(--on-surface);
    }

    .ds-section--section {
      background: var(--surface-secondary);
      color: var(--on-surface);
    }

    .ds-section--inverse {
      background: var(--surface-inverse);
      color: var(--on-inverse);
    }

    /* #400000 = brand DS BearingPoint — pas de token disponible */
    .ds-section--brand {
      background: #400000;
      color: #ffffff;
    }

    /* ── Padding vertical ─────────────────────────────────────── */
    .ds-section--py-sm { padding: var(--section-py-sm) 0; }
    .ds-section--py-md { padding: var(--section-py-md) 0; }
    .ds-section--py-lg { padding: var(--section-py-lg) 0; }
    .ds-section--py-xl { padding: var(--section-py-xl) 0; }

    /* ── Container ────────────────────────────────────────────── */
    .ds-section__container {
      margin: 0 auto;
      padding: 0 var(--pad-x);

      @media (max-width: 768px) {
        padding: 0 var(--pad-x-sm);
      }
    }

    .ds-section__container--narrow { max-width: var(--max-narrow); }
    .ds-section__container--default { max-width: var(--max); }
    .ds-section__container--wide { max-width: var(--max-wide); }
    .ds-section__container--full { max-width: 100%; }

    /* ── Header ───────────────────────────────────────────────── */
    .ds-section__header {
      margin-bottom: 48px;

      &.ds-section__header--no-content {
        margin-bottom: 0;
      }
    }

    .ds-section__header--left { text-align: left; }
    .ds-section__header--center { text-align: center; }

    .ds-section__text {
      &--left { max-width: 780px; }
      &--center { max-width: 880px; margin: 0 auto; }
    }

    /* ── Eyebrow ──────────────────────────────────────────────── */
    .ds-section__eyebrow {
      font-family: var(--f-mono);
      font-size: 11px;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      margin-bottom: 14px;

      &--page,
      &--section {
        color: #CC2831;
      }

      &--inverse,
      &--brand {
        color: #FF6B6B;
      }
    }

    /* ── Title ────────────────────────────────────────────────── */
    .ds-section__title {
      font-family: var(--f-display);
      font-size: clamp(28px, 3vw, 44px);
      font-weight: 600;
      line-height: 1.15;
      letter-spacing: -0.015em;
      margin: 0 0 18px;
      text-wrap: balance;
    }

    /* ── Lead ─────────────────────────────────────────────────── */
    .ds-section__lead {
      font-size: 18px;
      line-height: 1.55;
      margin: 0;
      text-wrap: pretty;

      &--page,
      &--section {
        color: var(--on-surface-muted);
      }

      &--inverse {
        color: var(--on-inverse-muted);
      }

      &--brand {
        color: rgba(255, 255, 255, 0.75);
      }
    }
  `],
})
export class DsSectionComponent {
  readonly eyebrow = input<string | undefined>(undefined);
  readonly title = input<string | undefined>(undefined);
  readonly lead = input<string | undefined>(undefined);
  readonly align = input<DsSectionAlign>('left');
  readonly surface = input<DsSectionSurface>('page');
  readonly container = input<DsSectionContainer>('default');
  readonly py = input<DsSectionPy>('md');
  readonly id = input<string | undefined>(undefined);

  readonly sectionClasses = computed(() =>
    [`ds-section--${this.surface()}`, `ds-section--py-${this.py()}`].join(' ')
  );

  readonly containerClass = computed(() =>
    `ds-section__container--${this.container()}`
  );

  readonly headerClasses = computed(() => {
    const parts = [`ds-section__header--${this.align()}`];
    return parts.join(' ');
  });

  readonly textClasses = computed(() =>
    `ds-section__text--${this.align()}`
  );
}
